import { ApiRoutes } from './apiRoutes'

import { useApiHandler } from '@/hooks/useApiHandler'
import { Discount } from '@/types'
import {
    ApiConfig,
    ApiResponse,
    ErrorHandlingStrategy,
    MessageLevel
} from '@/types/api'
import { Aspirant, UTMSaveRequest } from '@/types/aspirants'
import { IsMinorFormData } from '@/types/forms/IsMinorFormData'
import { AcademicAdmission } from '@/types/forms/academicAdmission'
import { AuthorizationFormData } from '@/types/forms/authorizationForm'
import { BasicInfoFormData } from '@/types/forms/basicInfoForm'
import { DemographicFormData } from '@/types/forms/demographicForm'
import { EducationFormData } from '@/types/forms/educationForm'
import { EntryTestFormData } from '@/types/forms/entryTestForm'
import { FormStatus } from '@/types/forms/formStatus'
import { JobInformationFormData } from '@/types/forms/jobInformation'
import { MedicalFormData } from '@/types/forms/medicalFormData'
import { HomologationOrderPaymentResponse } from '@/types/homologation'
import { IcfesAuditEntry } from '@/types/icfes'

// eslint-disable-next-line max-lines-per-function
export function useAspirantsApi(config?: Partial<ApiConfig>) {
    const api = useApiHandler(ApiRoutes.ASPIRANTS, config)
    const silentApi = useApiHandler(ApiRoutes.ASPIRANTS, {
        messageLevel: MessageLevel.NONE,
        errorHandling: ErrorHandlingStrategy.RETURN
    })

    return {
        getAspirant: (id: number): Promise<ApiResponse<Aspirant>> =>
            api.get<Aspirant>(`/${id}`),

        updateAspirant: (
            id: number,
            data: Partial<Aspirant>
        ): Promise<ApiResponse<Aspirant>> => api.put<Aspirant>(`/${id}`, data),

        getAspirantDocuments: (id: number): Promise<ApiResponse<Aspirant>> =>
            api.get<Aspirant>(`/${id}/documents`),

        submitApplication: (id: number): Promise<ApiResponse<void>> =>
            api.post<void>(`/${id}/submit`),

        getStatusForms: (
            idAspirant: number,
            idInscription: number | null
        ): Promise<ApiResponse<FormStatus>> =>
            api.get<FormStatus>(
                `/aspirants/${idAspirant}/forms-status?idInscription=${idInscription}`
            ),

        getAuthorizationForm: (
            idAspirant: number
        ): Promise<ApiResponse<AuthorizationFormData>> =>
            api.get<AuthorizationFormData>(
                `/aspirants/${idAspirant}/form-authorization`
            ),

        getIsMinorForm: (
            idAspirant: number
        ): Promise<ApiResponse<IsMinorFormData>> =>
            api.get<IsMinorFormData>(
                `/aspirants/${idAspirant}/is-minor-authorization`
            ),

        postIsMinorForm: (
            idAspirant: number,
            data?: undefined,
            queryParams?: { isMinorAuthorization: boolean }
        ): Promise<ApiResponse<IsMinorFormData>> =>
            api.post<IsMinorFormData>(
                `/aspirants/${idAspirant}/is-minor-authorization?isMinorAuthorization=${queryParams?.isMinorAuthorization}`,
                data
            ),

        saveAuthorizationForm: (
            idAspirant: number,
            data: AuthorizationFormData
        ): Promise<ApiResponse<AuthorizationFormData>> => {
            const dataToSend = {
                ...data,
                updatedBy: idAspirant
            }

            return api.post<AuthorizationFormData>(
                `/aspirants/${idAspirant}/form-authorization`,
                dataToSend
            )
        },

        getBasicData: (
            idAspirant: number
        ): Promise<ApiResponse<BasicInfoFormData>> =>
            api.get<BasicInfoFormData>(`/aspirants/${idAspirant}/basic-data`),

        getAcademicAdmission: (
            idAspirant: number,
            idInscription?: number | null
        ): Promise<ApiResponse<AcademicAdmission>> => {
            const url = idInscription
                ? `/aspirants/${idAspirant}/academic-admission-data?idInscription=${idInscription}`
                : `/aspirants/${idAspirant}/academic-admission-data`

            return api.get<AcademicAdmission>(url)
        },

        postBasicData: (
            idAspirant: number,
            data: BasicInfoFormData,
            agent: number
        ): Promise<ApiResponse<BasicInfoFormData>> =>
            api.post<BasicInfoFormData>(
                `/aspirants/${idAspirant}/basic-data`,
                data,
                {
                    headers: {
                        Agent: agent
                    }
                }
            ),

        getDemographicData: (
            idAspirant: number
        ): Promise<ApiResponse<DemographicFormData>> =>
            api.get<DemographicFormData>(
                `/aspirants/${idAspirant}/demographic-data`
            ),

        postDemographicData: (
            idAspirant: number,
            data: DemographicFormData,
            agent: number
        ): Promise<ApiResponse<DemographicFormData>> =>
            api.post<DemographicFormData>(
                `/aspirants/${idAspirant}/demographic-data`,
                data,
                {
                    headers: {
                        Agent: agent
                    }
                }
            ),

        getMedicalData: (
            idAspirant: number
        ): Promise<ApiResponse<MedicalFormData>> =>
            api.get<MedicalFormData>(`/aspirants/${idAspirant}/medical-data`),

        postMedicalData: (
            idAspirant: number,
            data: MedicalFormData,
            agent: number
        ): Promise<ApiResponse<MedicalFormData>> =>
            api.post<MedicalFormData>(
                `/aspirants/${idAspirant}/medical-data`,
                data,
                {
                    headers: {
                        Agent: agent
                    }
                }
            ),

        getInclusionData: (idAspirant: number): Promise<ApiResponse<any>> =>
            api.get<any>(`/aspirants/${idAspirant}/inclusion-data`),

        getDiscountsData: (
            idInscription: number
        ): Promise<ApiResponse<Discount>> =>
            api.get<Discount>(`/aspirants/${idInscription}/discounts-data`),

        postDiscountsData: (
            idInscription: number,
            idAspirant: number,
            data: Discount,
            agent: number
        ): Promise<ApiResponse<string>> =>
            api.post<string>(
                `/aspirants/${idInscription}/discounts-data?idAspirant=${idAspirant}`,
                data,
                {
                    headers: {
                        Agent: agent
                    }
                }
            ),

        postInclusionData: (
            idAspirant: number,
            data: any,
            p0: number
        ): Promise<ApiResponse<any>> =>
            api.post<any>(`/aspirants/${idAspirant}/inclusion-data`, data),

        getEducationData: (
            idAspirant: number
        ): Promise<ApiResponse<EducationFormData>> =>
            api.get<EducationFormData>(`/aspirants/education/${idAspirant}`),

        postEducationData: (
            idAspirant: number,
            data: EducationFormData,
            agent: number
        ): Promise<ApiResponse<EducationFormData>> =>
            api.post<EducationFormData>(
                `/aspirants/education/${idAspirant}`,
                data,
                {
                    headers: {
                        Agent: agent
                    }
                }
            ),
        getEmergencyContactData: (
            idAspirant: number
        ): Promise<ApiResponse<any>> =>
            api.get<any>(`/aspirants/${idAspirant}/emergency-contact-data`),

        postEmergencyContactData: (
            idAspirant: number,
            data: any,
            agent: number
        ): Promise<ApiResponse<any>> => {
            const dataToSend = {
                ...data,
                idAspirant: idAspirant
            }

            return api.post<any>(
                `/aspirants/${idAspirant}/emergency-contact-data`,
                dataToSend,
                {
                    headers: {
                        Agent: agent
                    }
                }
            )
        },
        getJobInformationData: (
            idAspirant: number
        ): Promise<ApiResponse<JobInformationFormData>> =>
            api.get<JobInformationFormData>(
                `/aspirants/${idAspirant}/laboral-information`
            ),

        postJobInformationData: (
            idAspirant: number,
            data: JobInformationFormData,
            agent: number
        ): Promise<ApiResponse<JobInformationFormData>> =>
            api.post<JobInformationFormData>(
                `/aspirants/${idAspirant}/laboral-information`,
                data,
                {
                    headers: {
                        Agent: agent
                    }
                }
            ),
        getPsychosocialData: (
            idAspirant: number,
            formId: number
        ): Promise<ApiResponse<EntryTestFormData[]>> =>
            api.get<EntryTestFormData[]>(
                `/forms/${formId}/aspirant/${idAspirant}`
            ),
        postPsychosocialData: (
            idAspirant: number,
            formId: number,
            idInscription: number,
            data: any[]
        ): Promise<ApiResponse<EntryTestFormData>> => {
            const dataToSend = {
                idForm: formId,
                idAspirant: idAspirant,
                idInscription: idInscription,
                responses: data
            }

            return api.post<EntryTestFormData>(`/forms/responses`, dataToSend)
        },
        getQuestionsByFormId: (
            formId: number
        ): Promise<ApiResponse<EntryTestFormData[]>> =>
            api.get<EntryTestFormData[]>(`/forms/${formId}`),
        //TODO: correct url and params
        getUploadFilesFormData: (
            idAspirant: number
        ): Promise<ApiResponse<any>> =>
            api.get<any>(`/aspirants-documents/${idAspirant}`),
        postUploadFilesFormData: (
            idAspirant: number,
            idDocumentType: number,
            data: any
        ): Promise<ApiResponse<any>> =>
            api.post<any>(
                `/aspirants-documents/${idAspirant}/upload/${idDocumentType}`,
                data
            ),

        postAcademicData: (
            idAspirant: number,
            data: AcademicAdmission,
            idInscription: string,
            agent: number
        ): Promise<ApiResponse<AcademicAdmission>> =>
            api.post<AcademicAdmission>(
                `/aspirants/${idAspirant}/academic-admission-data?idInscription=${idInscription}`,
                data,
                {
                    headers: {
                        Agent: agent
                    }
                }
            ),

        // ICFES endpoints (aspirants)
        getIcfesExams: (idAspirant: number): Promise<ApiResponse<any>> =>
            api.get<any>(`/aspirants/${idAspirant}/icfes-exams`),

        // Historial de cambios de la fecha ICFES (solo agentes)
        getIcfesAudit: (
            idAspirant: number,
            examId: number
        ): Promise<ApiResponse<IcfesAuditEntry[]>> =>
            api.get<IcfesAuditEntry[]>(
                `/aspirants/${idAspirant}/icfes-exams/${examId}/audit`,
                { params: { field: 'fecha_icfes' } }
            ),

        postIcfesForm: (
            idAspirant: number,
            payload: any,
            agent: number
        ): Promise<ApiResponse<any>> =>
            api.post<any>(`/aspirants/${idAspirant}/icfes-data/form`, payload, {
                headers: { Agent: agent }
            }),

        getAuthorizationAspirantScope: (
            idAspirant: number
        ): Promise<ApiResponse<any>> =>
            api.get<any>(`/aspirants/aspirant/${idAspirant}/noInscription`),

        sendReviewEmailByDocument: (
            documentNumber: string,
            idInscription: number
        ): Promise<ApiResponse<null>> =>
            api.post<null>(
                `/aspirants/by-document/${documentNumber}/send-review-email`,
                undefined,
                {
                    params: { idInscription }
                }
            ),

        getAuthorizationAspirantByInscription: (
            idAspirant: number,
            idInscription: number
        ): Promise<ApiResponse<any>> =>
            api.get<any>(
                `/aspirants/aspirant/${idAspirant}/inscription/${idInscription}`
            ),

        postAuthorizationAspiranByScope: (data: {
            idAspirant: number
            idInscription?: number
            updatedBy: number
            authorizations: Array<{
                authorizationTypeId: number
                isAccepted: boolean
                valueText: string
            }>
        }): Promise<ApiResponse<any>> => api.post<any>(`/aspirants/form`, data),

        getAdmissionTestRegister: (
            idAspirant: number,
            idInscription: number,
            idForm: number
        ): Promise<ApiResponse<any>> =>
            api.get<any>(
                `/forms/${idForm}/aspirant/${idAspirant}/inscription/${idInscription}/responses`
            ),

        getisElegibleForDoubleProgram: (
            idAspirant: number
        ): Promise<ApiResponse<boolean>> =>
            api.get<boolean>(
                `/aspirants/aspirant-is-elegible-for-double-program/${idAspirant}`
            ),

        // Endpoint para validar elegibilidad tipo de ingreso "02"
        getisElegibleForAdmissionType02: (
            idAspirant: number
        ): Promise<ApiResponse<boolean>> =>
            api.get<boolean>(
                `/aspirants/aspirant-is-elegible-for-admission-type-02/${idAspirant}`
            ),

        // Endpoint para validar elegibilidad tipo de ingreso "04"
        getisElegibleForGraduateToOtherProgram: (
            idAspirant: number
        ): Promise<ApiResponse<boolean>> =>
            api.get<boolean>(`/aspirants/aspirant-is-graduated/${idAspirant}`),

        getEncuestaData: (idAspirant: number): Promise<ApiResponse<any>> =>
            api.get<any>(`/aspirants/${idAspirant}/encuesta-data`),

        saveEncuestaData: (
            idAspirant: number,
            data: {
                idAspirant: number
                zzAspectos: string
                zzMedio: string
                zzDetMedio: string
            },
            agent: number
        ): Promise<ApiResponse<any>> =>
            api.post<any>(`/aspirants/${idAspirant}/encuesta-data`, data, {
                headers: {
                    Agent: agent
                }
            }),

        getFinancialSponsor: (
            idInscription: number
        ): Promise<ApiResponse<any>> =>
            api.get<any>(
                `/aspirants/financial-sponsor/V1/getByInscription?inscriptionId=${idInscription}`
            ),

        getFinancialSponsorSilent: (
            idInscription: number
        ): Promise<ApiResponse<any>> =>
            silentApi.get<any>(
                `/aspirants/financial-sponsor/V1/getByInscription?inscriptionId=${idInscription}`
            ),

        createFinancialSponsor: (data: {
            inscriptionId: number
            actividadEconomicaPrincipal: string
            actividadEconomicaSecundaria?: string
            nombre: string
            tipoDocumento: string
            numeroDocumentoIdentidad: string
        }): Promise<ApiResponse<any>> =>
            api.post<any>(`/aspirants/financial-sponsor/V1/create`, data),

        sendAdmissionContinueEmail: (
            idInscription: string | number,
            claseOyente: number
        ): Promise<ApiResponse<void>> =>
            api.post<void>(
                `/aspirants/inscription/${idInscription}/send-admission-continue-email?claseOyente=${claseOyente}`
            ),

        sendAdmissionSuccessEmail: (
            idInscription: string | number,
            claseOyente: number
        ): Promise<ApiResponse<void>> =>
            api.post<void>(
                `/aspirants/inscription/${idInscription}/send-admission-success-email?claseOyente=${claseOyente}`
            ),

        saveUTM: (data: UTMSaveRequest): Promise<ApiResponse<void>> =>
            api.post<void>('/utm/save', data),

        getHomologationOrderPayment: (
            idAspirant: number,
            idInscription: number
        ): Promise<ApiResponse<HomologationOrderPaymentResponse[]>> =>
            api.get<HomologationOrderPaymentResponse[]>(
                `/aspirants/${idAspirant}/${idInscription}/homologation-payment-order`
            )
    }
}
